import React, { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FiChevronLeft, FiChevronRight, FiMapPin, FiStar } from 'react-icons/fi';
import { useTranslation } from 'react-i18next';
import { formatCurrency, safeArray } from '../utils/formatters';

const HotelCard = ({ hotel }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);

  const photoUrls = useMemo(() => safeArray(hotel?.photoUrls), [hotel?.photoUrls]);

  if (!hotel) return null;

  const goPrevious = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setCurrentPhotoIndex((value) => (value === 0 ? photoUrls.length - 1 : value - 1));
  };

  const goNext = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setCurrentPhotoIndex((value) => (value === photoUrls.length - 1 ? 0 : value + 1));
  };

  return (
    <Link to={`/hotel/${hotel.id}`} className="soft-card block overflow-hidden">
      <div className="relative">
        <img
          src={photoUrls[currentPhotoIndex] || '/hotel-placeholder.jpg'}
          alt={hotel.hotelName}
          className="h-56 w-full object-cover"
          onError={(event) => {
            event.currentTarget.src = '/hotel-placeholder.jpg';
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-slate-950/60 via-transparent to-transparent" />
        <div className="absolute left-4 top-4 flex flex-wrap gap-2">
          <span className="pill bg-white/90 text-slate-800">
            <FiStar className="text-amber-500" />
            {hotel.starRating || 0} {t('design.hotelCard.starLabel')}
          </span>
          <span className="pill bg-white/90 text-slate-800">{hotel.district || t('design.hotelCard.districtFallback')}</span>
        </div>
        {photoUrls.length > 1 ? (
          <>
            <button type="button" onClick={goPrevious} className="absolute left-4 top-1/2 -translate-y-1/2 rounded-full bg-white/90 p-2 text-slate-900 shadow-lg">
              <FiChevronLeft className="h-5 w-5" />
            </button>
            <button type="button" onClick={goNext} className="absolute right-4 top-1/2 -translate-y-1/2 rounded-full bg-white/90 p-2 text-slate-900 shadow-lg">
              <FiChevronRight className="h-5 w-5" />
            </button>
          </>
        ) : null}
      </div>

      <div className="space-y-5 p-5">
        <div className="space-y-2">
          <div className="flex items-start justify-between gap-4">
            <h3 className="font-serif text-2xl font-semibold text-slate-900">{hotel.hotelName}</h3>
            <span className="rounded-full bg-teal-50 px-3 py-1 text-xs font-semibold uppercase tracking-[0.18em] text-teal-700">
              {t('design.hotelCard.featured')}
            </span>
          </div>
          <p className="flex items-center gap-2 text-sm text-slate-600">
            <FiMapPin className="text-teal-700" />
            {hotel.hotelCity}, {hotel.district}
          </p>
        </div>

        <div className="rounded-3xl border border-stone-200 bg-stone-50/80 p-4">
          <p className="text-xs uppercase tracking-[0.2em] text-stone-500">{t('design.hotelCard.startingFrom')}</p>
          <div className="mt-2 flex items-end justify-between gap-4">
            <p className="font-serif text-3xl font-semibold text-slate-900">{formatCurrency(hotel.advancePayment)}</p>
            <p className="text-sm text-slate-500">{t('hotels.perNight')}</p>
          </div>
        </div>

        <div className="flex flex-wrap gap-3">
          <span className="pill">{t('design.hotelCard.flexibleBooking')}</span>
          <span className="pill">{t('design.hotelCard.localConcierge')}</span>
        </div>

        <div className="flex gap-3">
          <span className="btn-ghost flex-1">{t('design.hotelCard.viewDetails')}</span>
          <button
            type="button"
            onClick={(event) => {
              event.preventDefault();
              navigate(`/reserve/${hotel.id}`);
            }}
            className="btn-primary flex-1"
          >
            {t('design.hotelCard.reserveNow')}
          </button>
        </div>
      </div>
    </Link>
  );
};

export default HotelCard;
